import { ISSUE_PANEL_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { Source } from '@atlassian/jira-forge-ui-types/src/common/types/analytics.tsx';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import {
	fireUiEvent,
	fireTrackEvent,
	fireOperationalEvent,
} from '@atlassian/jira-forge-ui-analytics/src/common/utils/consumers/index.tsx';
import {
	getEventId,
	getEventIdOld,
} from '@atlassian/jira-forge-ui-analytics/src/common/utils/index.tsx';
import { forgeModulesConcurrentLoadTime } from '@atlassian/jira-forge-ui-analytics/src/common/utils/performance-analytics/main.tsx';
import {
	MODULE_TYPE_TRIGGER,
	MODULE_TYPE_EXTENSION,
	UI_ACTIONS,
	TRACK_ACTIONS,
	OPERATIONAL_ACTIONS,
} from '@atlassian/jira-forge-ui-analytics/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';

const moduleEventId = getEventIdOld({
	module: ISSUE_PANEL_MODULE,
	moduleType: MODULE_TYPE_EXTENSION,
});

const triggerEventId = getEventIdOld({
	module: ISSUE_PANEL_MODULE,
	moduleType: MODULE_TYPE_TRIGGER,
});

// This method will generate the same metric key as error boundary
export const fireOperationalFailedEvent = (source?: Source, attributes?: Attributes) =>
	fireOperationalEvent(OPERATIONAL_ACTIONS.FAILED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireUiActionClickedEvent = (source?: Source, attributes?: Attributes) =>
	fireUiEvent(UI_ACTIONS.ACTION_CLICKED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireTrackActionSucceedEvent = (source?: Source, attributes?: Attributes) =>
	fireTrackEvent(TRACK_ACTIONS.ACTION_SUCCEED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireOperationalActionFailedEvent = (source?: Source, attributes?: Attributes) =>
	fireOperationalEvent(OPERATIONAL_ACTIONS.ACTION_FAILED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireTrackSavedEvent = (source?: Source, attributes?: Attributes) =>
	fireTrackEvent(TRACK_ACTIONS.SAVED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireOperationalSaveFailedEvent = (source?: Source, attributes?: Attributes) =>
	fireOperationalEvent(OPERATIONAL_ACTIONS.SAVE_FAILED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireUiRemoveClickedEvent = (source?: Source, attributes?: Attributes) =>
	fireUiEvent(UI_ACTIONS.REMOVE_CLICKED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireTrackRemovedEvent = (source?: Source, attributes?: Attributes) =>
	fireTrackEvent(TRACK_ACTIONS.REMOVED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireOperationalRemoveFailedEvent = (source?: Source, attributes?: Attributes) =>
	fireOperationalEvent(OPERATIONAL_ACTIONS.REMOVE_FAILED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireUiTriggerSelectedEvent = (
	extensionId: string,
	source?: Source,
	attributes?: Attributes,
) => {
	forgeModulesConcurrentLoadTime(extensionId).start({ startTime: performance.now() });

	return fireUiEvent(UI_ACTIONS.SELECTED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_TRIGGER,
				})
			: triggerEventId,
		source,
		attributes,
	});
};

export const fireUiCollapseExpandClickedEvent = (source?: Source, attributes?: Attributes) =>
	fireUiEvent(UI_ACTIONS.COLLAPSE_EXPAND_CLICKED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireUiPinUnpinActionClickedEvent = (source?: Source, attributes?: Attributes) =>
	fireUiEvent(UI_ACTIONS.PIN_UNPIN_CLICKED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireTrackUpdateSucceedEvent = (source?: Source, attributes?: Attributes) =>
	fireTrackEvent(TRACK_ACTIONS.UPDATE_SUCCEED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});

export const fireOperationalUpdateFailedEvent = (source?: Source, attributes?: Attributes) =>
	fireOperationalEvent(OPERATIONAL_ACTIONS.UPDATE_FAILED, {
		id: fg('ditto-fcf-support-new-manifest-on-frontend')
			? getEventId({
					module: ISSUE_PANEL_MODULE,
					moduleType: MODULE_TYPE_EXTENSION,
				})
			: moduleEventId,
		source,
		attributes,
	});
